/**
 * №22.3 (D-CH0/D-CH1) — the docked collaboration panel's state.
 *
 * Chat is not a feature reached by navigation — it is a persistent layer of the
 * App Shell. This context owns the ONE panel that lives in the third grid column
 * of `AppShellLayout`, present on every `/app` surface, mounted once and surviving
 * all navigation (the SPA invariant, D-CH3). It holds:
 *
 *  - `expanded` — docked by default (D-CH0), collapsible to an edge tab; persisted
 *    per-workspace (localStorage), mirroring the console `ChatRailContext`.
 *  - `focusedTask` — the task-run STREAM the panel is showing, or `null` = the
 *    route-scoped "Here" chat. Clicking a card / "view thread" anywhere focuses the
 *    panel on that task (D-CH4: retarget to the panel, never a route).
 *  - `answeredNonce` / `notifyAnswered` — the answer choreography hand-off (D-CH2):
 *    answering a card bumps this so the panel reveals the receipt as it streams,
 *    while the card resolves in place on the surface (no navigation, no reflow).
 *  - `draft` / `postNonce` / `postToScope` — the hand-off that starts a scope chat.
 *    22.3's bottom-bar launcher posted through it; S3 deleted that bar (§3 ②) and the
 *    rail's own composer is the sole caller now. The mechanism survives the caller
 *    because it is what makes the FIRST typed turn appear in a conversation that did
 *    not exist a moment ago — one queue, whoever is holding the keyboard.
 *
 * Deliberately NOT the console `ChatRailContext`: that rail is the maker cockpit's
 * chat-session surface; this is the operational streams dock. They share the
 * primitives (`useV3ChatRuntime`, `AgentThread`), not the state.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../../../lib/hooks/use-auth";
import type { RouteContext, RouteAnchor } from "../../routes/app/app-scope";
import type { StreamActor } from "../../../lib/types";

/**
 * №22.3.1 (D-AF2, complaint ②) — a scope chat's identity is FROZEN when it starts,
 * so a PINNED conversation keeps its anchor + actor + backing thread as you navigate
 * (the bug was: the chip rotated with the route while one global chat accumulated
 * under it). The composer captures this from the route it posted at.
 */
export interface ChatSnapshot {
  ctx: RouteContext;
  anchor: RouteAnchor;
  chip: string;
  actor: StreamActor;
}

/**
 * №22.3.2 (F2) — HOW a card / thread got into a CONVERSATION. Ruled: rollup
 * projections (the stream and history layers — receipts and thread rows you READ)
 * are never conversation turns, and a fresh conversation at ANY anchor starts EMPTY.
 * A card enters a conversation in exactly three ways, and there is no fourth:
 *
 *  - `"clicked"` — the user clicked it somewhere (the D-CH2 choreography): a stream
 *    row, a Needs-You card's "view thread", a history row, a run's continuation.
 *  - `"typed"`   — the user typed about it (the bar / the panel composer).
 *  - `"raised"`  — the actor raised it INSIDE its own run thread, while that thread
 *    is being watched. This adds no entry of its own: the conversation was already
 *    entered by one of the two above, and the card arrives as part of the thread.
 *
 * The panel's conversation state is private to this provider and writable ONLY by
 * `focusTask` / `notifyAnswered` (clicked) and `postToScope` (typed) — each of which
 * stamps an entry below. Nothing else may make the panel speak: not navigation, not
 * a stream poll, not a seeded Needs-You. That is the whole of "the panel speaks only
 * when spoken to".
 */
export type ConversationEntry = "clicked" | "typed";

export interface ConversationEntryRecord {
  via: ConversationEntry;
  /** What was entered: a task's run thread, or a fresh scope chat. */
  target: "task" | "scope";
  id?: string;
}

/**
 * Mirrored onto `window` so a browser journey can assert the invariant directly
 * ("this session entered a conversation N times, each attributed"). Read-only
 * evidence — nothing in the app reads it back.
 */
declare global {
  interface Window { __KUVIO_CHAT_ENTRIES__?: ConversationEntryRecord[] }
}

function publishEntries(list: ConversationEntryRecord[]): void {
  try { window.__KUVIO_CHAT_ENTRIES__ = list; } catch { /* SSR / locked-down window */ }
}

/** The task-run stream the panel is focused on (anchor=task, actor=agent — D-CV9). */
export interface PanelTaskFocus {
  taskId: string;
  agentId?: string;
  agentName?: string;
  /** "Patel — settlement · Exchange" — the anchor line for the panel header. */
  label?: string;
  /** №22.3.1 (D-AF5) — true when this focus came from a card answer / continuation,
   *  so the stream opens with a "— now in: {task} · with {actor} —" transition row. */
  viaAnswer?: boolean;
}

/**
 * №29 (D-L1) — which section of the WORK RAIL is open. The rail replaced the panel's
 * frame in S0, so "is the right zone open?" (`expanded`) and "which tab of it?" live
 * in the same place: one right-zone state, as there was one panel state before.
 * The literal union is duplicated from `WorkRail`'s `RailTab` on purpose — this
 * provider must not import a component (it is mounted above the whole shell).
 */
export type RailTabId = "chat" | "plan" | "context" | "receipts" | "memory";

interface CoPanelValue {
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  toggle: () => void;

  /** №29 — the open work-rail tab. `chat` is the default: it is where the 22.x
   *  contracts live, and on a chat SUBJECT the rail falls back to its first tab. */
  railTab: RailTabId;
  setRailTab: (t: RailTabId) => void;

  /** The focused task-run stream; null = the anchor stream or the scope chat. */
  focusedTask: PanelTaskFocus | null;
  /** Open the panel and focus a task's stream (D-CH4 — no navigation). */
  focusTask: (focus: PanelTaskFocus) => void;
  /** Back to the route's anchor STREAM (the default @scope view — D-CH1). */
  showStream: () => void;
  /** True when a composer posted an AI chat turn (the scope-chat mode). */
  chatActive: boolean;
  /** The FROZEN identity of the active scope chat (D-AF2, complaint ②) — the panel
   *  header + routing read this, not the live route, so a pinned chat holds together. */
  chatSnapshot: ChatSnapshot | null;

  // №22.3.1 (D-AF2) — the FOLLOW/PIN state machine. FOLLOW (default): navigation
  // re-anchors the panel. PINNED: navigation no longer swaps it (chip gains 📌).
  // Auto-pin while a run streams / the composer holds text (never yank mid-thought).
  /** True when the current conversation is held against navigation (manual or auto). */
  pinned: boolean;
  /** True only for a MANUAL pin (the 📌 the user toggled), vs an auto-pin. */
  manualPinned: boolean;
  /** Toggle the manual pin. Unpinning returns to follow (re-anchors to the route). */
  togglePin: () => void;
  /** A child reports "a run is streaming / the composer holds text" → auto-pin. */
  setBusy: (v: boolean) => void;
  /** FOLLOW: re-anchor to the route (clear focus/chat/history). Called on nav when
   *  not pinned, and by "return to here ↩" / unpin. */
  follow: () => void;

  // №22.3.1 (D-AF3 / P1) — the HISTORY layer: a LIST (no composer) of the anchor's
  // conversations, resumable. The 🕘 clock toggles it; selecting a row / ✕ returns.
  historyOpen: boolean;
  toggleHistory: () => void;
  closeHistory: () => void;

  /** №22.3.2 (F2) — the attributed log of every CONVERSATION entry this session, in
   *  order. Exactly three entries are legal (see `ConversationEntry`); navigation, a
   *  stream poll and a seeded Needs-You must add none. The CI journey reads it. */
  entries: readonly ConversationEntryRecord[];

  /** D-CH2 — bumped when a card is answered so the panel reveals the receipt. */
  answeredNonce: number;
  /** Focus the answered task + trigger the choreography reveal (one call). */
  notifyAnswered: (focus: PanelTaskFocus) => void;

  /**
   * №29 (S4.3) — the conversation you had at an anchor, so coming back finds it.
   *
   * David: *"I entered a chat and got a reply, switched away, navigated back — chat was
   * clear and no history of the recent chat."* He was right, and it was worse than it
   * looked: `ScopeChat` minted `crypto.randomUUID()` per MOUNT, so the conversation still
   * existed server-side and **nothing could ever address it again**. Not hidden — orphaned,
   * once per navigation.
   *
   * The id is remembered per anchor for the session, and returning re-opens that
   * conversation rather than starting a nameless new one.
   *
   * **This is not 22.3.2 F2b being reopened.** F2b ruled that a conversation opens EMPTY at
   * an anchor — no rollup, no receipts, nothing the panel decided to say. That is about the
   * panel speaking first. Showing you the conversation *you* typed, at the anchor you typed
   * it at, is the panel remembering rather than speaking; a fresh anchor still opens empty.
   */
  recallConversation: (anchorKey: string) => { id: string; snapshot: ChatSnapshot } | null;
  /** Re-open a remembered conversation (navigation back to its anchor). */
  resumeConversation: (anchorKey: string) => boolean;

  /** Composer → conversation hand-off (D-CH1). The rail's composer posts here. */
  draft: string | null;
  setDraft: (v: string | null) => void;
  postNonce: number;
  /** Open the panel, drop to the route scope (frozen via `snapshot`), and queue `text`. */
  postToScope: (text: string, snapshot: ChatSnapshot) => void;
}

const CoPanelContext = createContext<CoPanelValue | null>(null);

function storageKeyFor(workspaceId: string | null | undefined): string | null {
  return workspaceId ? `kuvio_copanel_expanded_${workspaceId}` : null;
}

/** The raw persisted preference for a key, or null when none has been saved. */
function readExpandedRaw(key: string | null): boolean | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? null : raw === "1";
  } catch {
    return null;
  }
}

function loadExpanded(key: string | null): boolean {
  return readExpandedRaw(key) ?? true; // D-CH0 — docked by default
}

function persistExpanded(key: string | null, value: boolean): void {
  if (!key) return;
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/** The key a conversation is remembered under: the anchor, as the user recognises it. */
export function anchorKeyOf(anchor: RouteAnchor): string {
  return `${anchor.kind}:${anchor.id ?? ""}`;
}

export function CoPanelProvider({ children }: { children: ReactNode }) {
  const { currentWorkspaceId } = useAuth();
  const key = storageKeyFor(currentWorkspaceId);

  const [expanded, setExpandedState] = useState<boolean>(() => loadExpanded(key));
  const [railTab, setRailTab] = useState<RailTabId>("chat");
  const [focusedTask, setFocusedTask] = useState<PanelTaskFocus | null>(null);
  const [chatActive, setChatActive] = useState(false);
  const [chatSnapshot, setChatSnapshot] = useState<ChatSnapshot | null>(null);
  const [manualPinned, setManualPinned] = useState(false);
  const [busy, setBusyState] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [answeredNonce, setAnsweredNonce] = useState(0);
  const [draft, setDraft] = useState<string | null>(null);
  const [postNonce, setPostNonce] = useState(0);
  // №29 (S4.3) — conversations by anchor, for the life of the session. A ref, not state:
  // nothing renders off the map itself (the ACTIVE conversation is `chatSnapshot`), so
  // writing to it must not re-render the whole shell on every keystroke-started chat.
  const convos = useRef<Record<string, { id: string; snapshot: ChatSnapshot }>>({});
  // №22.3.2 (F2) — every conversation entry, attributed. See `ConversationEntry`.
  const [entries, setEntries] = useState<ConversationEntryRecord[]>([]);
  const enter = useCallback((record: ConversationEntryRecord) => {
    setEntries((cur) => {
      const next = [...cur, record];
      publishEntries(next);
      return next;
    });
  }, []);

  // Re-hydrate the expand preference when the workspace changes — the React-blessed
  // "store info from previous renders" pattern (setState during render, no effect,
  // no cascade). https://react.dev/reference/react/useState#storing-information-from-previous-renders
  // №22.3.1 — adopt the new key's preference ONLY when one is persisted; otherwise
  // keep the current state. This stops the initial null→wid auth resolution from
  // clobbering an in-flight collapse with the default (docked).
  const [seenKey, setSeenKey] = useState(key);
  if (seenKey !== key) {
    setSeenKey(key);
    const persisted = readExpandedRaw(key);
    if (persisted !== null) setExpandedState(persisted);
  }

  const setExpanded = useCallback(
    (value: boolean) => {
      setExpandedState(value);
      persistExpanded(key, value);
    },
    [key],
  );

  const toggle = useCallback(() => {
    setExpandedState((cur) => {
      const next = !cur;
      persistExpanded(key, next);
      return next;
    });
  }, [key]);

  // Entry path #1 (F2) — the user CLICKED a row / card / continuation somewhere.
  const focusTask = useCallback(
    (focus: PanelTaskFocus) => {
      enter({ via: "clicked", target: "task", id: focus.taskId });
      setFocusedTask(focus);
      setChatActive(false);
      setChatSnapshot(null);
      setHistoryOpen(false);
      setExpanded(true);
      setRailTab("chat"); // №29 — clicking a card opens the rail's Chat tab, not a route
    },
    [setExpanded, enter],
  );

  const showStream = useCallback(() => {
    setFocusedTask(null);
    setChatActive(false);
    setChatSnapshot(null);
    setHistoryOpen(false);
  }, []);

  // FOLLOW (D-AF2) — re-anchor to the route: drop the focused task / scope chat / pin
  // / history so the panel shows the current @scope's stream. Called on navigation
  // (when not pinned), and by unpin / "return to here ↩".
  const follow = useCallback(() => {
    setFocusedTask(null);
    setChatActive(false);
    setChatSnapshot(null);
    setHistoryOpen(false);
    setManualPinned(false);
  }, []);

  const togglePin = useCallback(() => {
    setManualPinned((cur) => {
      const next = !cur;
      // Unpinning returns to follow (re-anchor to the current route).
      if (!next) { setFocusedTask(null); setChatActive(false); setChatSnapshot(null); setHistoryOpen(false); }
      return next;
    });
  }, []);

  const setBusy = useCallback((v: boolean) => setBusyState(v), []);

  const toggleHistory = useCallback(() => setHistoryOpen((v) => !v), []);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);

  const notifyAnswered = useCallback(
    (focus: PanelTaskFocus) => {
      // D-AF5 — a card answer moves you into the task thread; announce the transition.
      // Entry path #1 (F2) — answering IS a click on the card.
      enter({ via: "clicked", target: "task", id: focus.taskId });
      setFocusedTask({ ...focus, viaAnswer: true });
      setChatActive(false);
      setChatSnapshot(null);
      setHistoryOpen(false);
      setExpanded(true);
      setRailTab("chat"); // №29 — the answer choreography plays in the rail's Chat tab
      setAnsweredNonce((n) => n + 1);
    },
    [setExpanded, enter],
  );

  // Entry path #2 (F2) — the user TYPED. The conversation this opens starts EMPTY:
  // the only turn in it is the one being sent.
  const postToScope = useCallback(
    (text: string, snapshot: ChatSnapshot) => {
      enter({ via: "typed", target: "scope" });
      // №29 (S4.3) — mint the conversation's id ONCE, here, and remember it against the
      // anchor. It used to be minted inside `ScopeChat` with `crypto.randomUUID()` on every
      // mount, which meant navigating away orphaned the conversation: it survived on the
      // server and no id existed to reach it. Keyed on the anchor because that is what the
      // user recognises — "the chat I had about this matter".
      const key = anchorKeyOf(snapshot.anchor);
      convos.current[key] ??= { id: `${key}:${crypto.randomUUID()}`, snapshot };
      setFocusedTask(null);
      setChatActive(true);
      setChatSnapshot(snapshot); // freeze the anchor+actor the chat is WITH (D-AF2)
      setHistoryOpen(false);
      setExpanded(true);
      setRailTab("chat"); // №29 — typing opens the conversation, wherever the rail was
      setDraft(text);
      setPostNonce((n) => n + 1);
    },
    [setExpanded, enter],
  );

  const recallConversation = useCallback(
    (anchorKey: string) => convos.current[anchorKey] ?? null,
    [],
  );

  /** Navigation back to an anchor you have talked at: re-open that conversation. */
  const resumeConversation = useCallback((anchorKey: string) => {
    const found = convos.current[anchorKey];
    if (!found) return false;
    setFocusedTask(null);
    setChatActive(true);
    setChatSnapshot(found.snapshot);
    setHistoryOpen(false);
    return true;
  }, []);

  // Auto-pin is derived: a manual pin, OR a run streaming / composer holding text.
  const pinned = manualPinned || busy;

  const value = useMemo<CoPanelValue>(
    () => ({
      expanded, setExpanded, toggle,
      railTab, setRailTab,
      focusedTask, focusTask, showStream, chatActive, chatSnapshot,
      pinned, manualPinned, togglePin, setBusy, follow,
      historyOpen, toggleHistory, closeHistory,
      entries, answeredNonce, notifyAnswered,
      recallConversation, resumeConversation,
      draft, setDraft, postNonce, postToScope,
    }),
    [expanded, setExpanded, toggle, railTab, focusedTask, focusTask, showStream, chatActive, chatSnapshot, pinned, manualPinned, togglePin, setBusy, follow, historyOpen, toggleHistory, closeHistory, entries, answeredNonce, notifyAnswered, recallConversation, resumeConversation, draft, postNonce, postToScope],
  );

  return <CoPanelContext.Provider value={value}>{children}</CoPanelContext.Provider>;
}

export function useCoPanel(): CoPanelValue {
  const ctx = useContext(CoPanelContext);
  if (!ctx) throw new Error("useCoPanel must be used within a CoPanelProvider");
  return ctx;
}

/**
 * Non-throwing variant — for the ONE `DecisionCard` (R1), which renders both
 * inside the App Shell (panel present) and in credential-free previews (no
 * provider). Returns null when there's no panel; the card falls back to its
 * route link.
 */
export function useCoPanelOptional(): CoPanelValue | null {
  return useContext(CoPanelContext);
}
